
import { SearchBookingOverview, SearchHotelPension, SearchHotelRoom, SearchTravelRequest } from '@/types/models';
import { computed, defineComponent, PropType, ref } from 'vue';

import EasyDrop from "@/components/EasyDrop.vue";
import DotSpinner from "@/components/EasySpinners/DotSpinner.vue";

import { format } from "date-fns";
import * as Locales from 'date-fns/locale';
import { useStore } from 'vuex';
import { LocaleStrings } from '@/types/paramModels';

export default defineComponent({
    name: "BookingOverviewActive",
    components: {
        EasyDrop,
        DotSpinner
    },
    props: {
        bookingOverview: {
            type: Object as PropType<SearchBookingOverview>,
            required: true
        },
        searchRequest: {
            type: Object as PropType<SearchTravelRequest>,
            required: true
        },
        searchQuery: {
            type: Object,
            required: true
        },
        pageUrls: {
            type: Object,
            required: true
        },
        selectedRooms: {
            type: Array as PropType<Array<SearchHotelRoom>>,
            required: true
        },
        pageTexts: {
            type: Object,
            required: true
        },
        localeStrings: {
            type: Object as PropType<LocaleStrings>,
            required: true
        }
    },
    setup(_props) {
        const store = useStore();

        const currentViewName = computed(() => store.getters['ApplicationModule/getCurrentViewName']);
        const searchBookingOverview = computed(() => _props.bookingOverview);

        const compSelectedRooms = computed(() => _props.selectedRooms);
        

        const isActive = ref(false);
        const tabletActive = ref(false);

        const adultsCount = computed(() => _props.bookingOverview.paxRooms.reduce((acc, { searchRoom }) => acc + searchRoom.adults.length, 0));
        const childrenCount = computed(() => _props.bookingOverview.paxRooms.reduce((acc, { searchRoom }) => acc + searchRoom.children.length, 0));

        const selectedPensions = computed(() => {
            let pensions = Array<SearchHotelPension>();
            for (const room of _props.selectedRooms) {
                const selectedPension = room.pensions.find((pension) => {
                    return pension.isSelected;
                });
                if (selectedPension) pensions.push(selectedPension);
            }
            return pensions;
        });

        const selectedTransfer = computed(() => {
            return _props.bookingOverview.transfers.find(x => x.isSelected);
        })

        const formatDate = (dateString: string) => {
            return format(new Date(dateString.slice(0, dateString.indexOf("T"))), "do MMM yyyy", { locale: Locales[_props.localeStrings.name] });
        }
        
        const formatDateTime = (dateString: string) => {
            const date = formatDate(dateString);
            const time = dateString.slice(dateString.indexOf("T") + 1, dateString.indexOf("T") + 6);
            return _props.pageTexts.hotelPageGeneralTimeFormat.trim().length > 0 ? date + " " + _props.pageTexts.hotelPageGeneralTimeFormat.trim() + " " + time : date + " " + time;
        };

        const resetPensionIndex = () => {
            store.dispatch("ApplicationModule/resetPensionIndex");
        }

        const navigatePensionIndex = () => {
            isActive.value = false;
            resetPensionIndex();
        }

        const resetAccommodationIndex = () => {
            store.dispatch("ApplicationModule/resetAccommodationIndex");
        }

        const navigateAccomodationIndex = () => {
            isActive.value = false;
            resetAccommodationIndex();
        }

        const resetPersonalDataIndex = () => {
            store.dispatch("ApplicationModule/resetPersonalDataIndex");
        }

        const navigatePersonalIndex = () => {
            isActive.value = false;
            resetPersonalDataIndex();
        }

        const adultSinglePlural = (count: number) => {
            return count > 1 ? _props.pageTexts['searchbarPersonPickerLabelAdults'] : _props.pageTexts['searchbarPersonPickerLabelAdult'];
        }

        const childSinglePlural = (count: number) => {
            return count > 1 ? _props.pageTexts['searchbarPersonPickerLabelChildren'] : _props.pageTexts['searchbarPersonPickerLabelChild'];
        }

        const isPersonalDataAvailable = computed(() => {
            if (searchBookingOverview.value.transfers.length && !searchBookingOverview.value.transfers.some(transfer => transfer.isSelected))
                return false;

            return true;
        })

        const isAllPersonalDataValid = computed(() => {
            for (const room of searchBookingOverview.value.bookingSelection.hotelRooms) {
                for (const [index, person] of room.searchRoom.adults.entries()) {
                    if (index === 0 && (!person.firstName
                        || !person.lastName
                        || !person.birthdate
                        || !person.street
                        || !person.postal
                        || !person.city
                        || !person.country
                        || !person.phone
                        || !person.email)) {
                        return false;
                    } else if (index > 0 && (!person.firstName || !person.lastName || !person.birthdate)) {
                        return false;
                    }

                }
                for (const person of room.searchRoom.children) {
                    if (!person.firstName || !person.lastName || !person.birthdate) {
                        return false;
                    }
                }
            }
            return true;
        })

        const isConfirmAndPayAvailable = computed(() => {
            return isPersonalDataAvailable.value && isAllPersonalDataValid.value;
        })

        const hotelLabel = computed(() => {
            return _props.pageTexts['hotelPageTravelOverviewLabelHotel'].replace(/\[[nights\]]+\]/g, _props.bookingOverview.travel.hotelNights);
        });

        const obligatoryTransferText = () => {
            let transferText = "";
            for (let i = 0; i < searchBookingOverview.value.transfers.length; i++) {
                if (i > 0) {
                    transferText += "/";
                }
                transferText += searchBookingOverview.value.transfers[i].name.toLowerCase();

            }
            return _props.pageTexts['hotelPageTravelOverviewLabelTransferAlert'].replace(/\[[transfer\]]+\]/g, transferText);
        }

        return {
            isActive,
            tabletActive,
            adultsCount,
            childrenCount,
            selectedPensions,
            selectedTransfer,
            formatDate,
            formatDateTime,
            resetPensionIndex,
            resetAccommodationIndex,
            resetPersonalDataIndex,
            currentViewName,
            adultSinglePlural,
            childSinglePlural,
            navigatePersonalIndex,
            navigatePensionIndex,
            navigateAccomodationIndex,
            isPersonalDataAvailable,
            isConfirmAndPayAvailable,
            hotelLabel,
            compSelectedRooms,
            obligatoryTransferText
        }
    },
})
